/**
 * Arrival news for the host support chat (#1064).
 *
 * Pure diff logic: compare the current whitelisted session facts against the
 * baseline from the host's previous visit and say what changed. No I/O here;
 * the widget owns fetching and storage. Everything in a NewsItem is data the
 * host already sees on their own dashboard.
 */

export type SessionFacts = {
    id: string;
    title: string;
    status: string;
    participantsResponded: number;
    participantsTotal: number;
    synthesisStatus: string;
    isSynthesisStale?: boolean;
};

/** What the widget remembers about one session between visits. */
export type BaselineFacts = {
    responded: number;
    synthesisStatus: string;
    stale: boolean;
};

export type NewsBaseline = {
    userId: string;
    facts: Record<string, BaselineFacts>;
};

export type NewsItem = {
    id: string;
    title: string;
    newResponses: number;
    synthesisReady: boolean;
    synthesisStale: boolean;
};

export const toBaseline = (userId: string, sessions: SessionFacts[]): NewsBaseline => {
    const facts: Record<string, BaselineFacts> = {};
    for (const session of sessions) {
        if (typeof session.id === 'string' && session.id.length > 0) {
            facts[session.id] = {
                responded: session.participantsResponded,
                synthesisStatus: session.synthesisStatus,
                stale: session.isSynthesisStale === true,
            };
        }
    }
    return { userId, facts };
};

/**
 * What changed since the baseline. Sessions the baseline has never seen are
 * NOT news — the host just created them — they only enter the next baseline.
 */
export const computeChatNews = (baseline: NewsBaseline, sessions: SessionFacts[]): NewsItem[] => {
    const news: NewsItem[] = [];
    for (const session of sessions) {
        const before = baseline.facts[session.id];
        if (!before) {
            continue;
        }
        const newResponses = Math.max(0, session.participantsResponded - before.responded);
        const synthesisReady = session.synthesisStatus === 'COMPLETED'
            && before.synthesisStatus !== 'COMPLETED'
            && session.isSynthesisStale !== true;
        const synthesisStale = session.isSynthesisStale === true && !before.stale;
        if (newResponses > 0 || synthesisReady || synthesisStale) {
            news.push({ id: session.id, title: session.title, newResponses, synthesisReady, synthesisStale });
        }
    }
    return news;
};
